import type React from 'react';
import {
  Button,
  FormControl,
  FormControlText,
  FormGroup,
  InputText,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Modalv2
} from '@itandi/itandi-bb-ui';
import { useForm } from 'react-hook-form';
import { useSWRConfig } from 'swr';
import { toast } from 'react-toastify';
import { generatePath } from '~/utils/generatePath';
import type { Deal } from '~/models/Deal';
import { updateNote } from '~/utils/api/vendors/deals/DealEstimationHeader';
import { isSuccess } from '~/utils/api/api_handler';

type Props = {
  deal: Deal;
  onClose: () => void;
  defaultNote: string;
};

export const NoteEditModal: React.FC<Props> = ({
  deal,
  onClose,
  defaultNote
}) => {
  const { mutate: globalMutate } = useSWRConfig();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, isDirty }
  } = useForm<{ note: string }>({
    defaultValues: { note: defaultNote }
  });

  const onSubmit = async (): Promise<void> => {
    const result = await updateNote(deal.id, watch('note'));
    if (isSuccess(result)) {
      toast.success('備考を保存しました。');
      globalMutate(
        generatePath('/vendors/deals/:id/deal_estimation_histories', {
          id: deal.id
        })
      );
      onClose();
    } else {
      toast.error('エラーが発生しました。');
    }
  };

  return (
    <Modalv2 isOpen onClose={onClose}>
      <ModalHeader title="備考の更新" />
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <FormGroup variant="vertical">
            <FormControl isInvalid={!!errors.note} label="備考">
              <InputText
                maxRows={8}
                resize="autoRows"
                rows={4}
                {...register('note')}
              />
              <FormControlText error={errors.note?.message} />
            </FormControl>
          </FormGroup>
        </ModalBody>
        <ModalFooter
          leftArea={
            <Button
              disabled={isSubmitting}
              onClick={onClose}
              variant="secondary"
            >
              キャンセル
            </Button>
          }
          rightArea={
            <Button
              disabled={!isDirty}
              loading={isSubmitting}
              type="submit"
              variant="primary"
            >
              保存
            </Button>
          }
        />
      </form>
    </Modalv2>
  );
};
